import { withApollo } from '@css-xp/base/graphql-provider';
import React from 'react';
import { Link } from 'react-router-dom';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import { PaymentMethodPanel, CreditCardPaymentMethod } from '@atlassian/commerce-payment-methods';
import {
  NotificationTypes,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  InfoPanel,
  ErrorComponents,
} from '@css-xp/base';
import { deletePaymentMethodById } from '@atlassian/temp-proxies';
import AssociatedBillingProfilesTable from './associated-billing-profiles-table';
import './remove-payment-method-sidebar.scss';
import { CreditCard } from '@css-xp/graphtypes/src/generated-types-new';

interface FormData {
  supportTicketLink: string;
  internalComment: string;
}

interface Props {
  refetch: () => Promise<void>;
  paymentMethod: CreditCard;
  transactionAccountId: string;
  transactionAccountDisplayName: string;
  transactionAccountNumber: string;
}

const RemovePaymentMethod = withApollo(
  ({
    refetch,
    paymentMethod,
    transactionAccountId,
    transactionAccountDisplayName,
    transactionAccountNumber,
  }: Props) => {
    const { error, isProcessing, submitForm, complete } = useFormSubmit({
      submitFormAction: deletePaymentMethodById,
    });

    const card = paymentMethod?.card;
    const paymentMethodId = paymentMethod?.id || '';
    const { closeSidebar } = useSidebar();
    const handleSubmit = (_formData: FormData) => {
      // _formData to be used when api supports metadata remove _ when it has to be used
      submitForm({ paymentMethodId, transactionAccountId });
    };

    const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

    const getSuccessMessage = () => {
      return (
        <div>
          {`Credit card ending with ${card?.last4Numbers} has been removed from all billing profiles where it is used.`}
          <div>
            <Link to="#audit-trail">View audit trail</Link>
          </div>
        </div>
      );
    };

    if (complete) {
      refetch();
      closeSidebar();
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withId('refund-processed-success')
          .withHeader('Payment method removed')
          .withMessage(getSuccessMessage())
          .withDismissAfter(5000)
          .build();
        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
      }
    }

    const cardDetails = {
      name: card?.cardHolderName,
      brand: card?.brand,
      expMonth: parseInt(card?.expirationMonth || ''),
      expYear: parseInt(card?.expirationYear || ''),
      last4: card?.last4Numbers,
    };

    return (
      <div className="remove-payment-method-sidebar">
        <h2 className="title">Remove payment method</h2>
        {error && (
          <div className="info-banners" data-testid="error-message">
            <InfoPanel
              type="error"
              message={
                <ErrorComponents.ErrorMessageOnInfoPanel
                  title="We're unable to delete the product data for the following reason"
                  message={error}
                />
              }
            />
          </div>
        )}
        <div className="billing-account-details" data-testid="billing-account-details">
          <div className="billing-account-item">
            <div className="bill-account-details-label">Billing account name</div>
            <div className="bill-account-details-text">{transactionAccountDisplayName}</div>
          </div>
          <div className="billing-account-item">
            <div className="bill-account-details-label">Billing account ID</div>
            <div className="bill-account-details-text">{transactionAccountNumber}</div>
          </div>
        </div>
        <div className="info-banners">
          <SectionMessage appearance="warning">
            This payment method will also be removed from other billing profiles where it is used.
          </SectionMessage>
        </div>
        <div className="payment-method-panel-container" data-testid="payment-method-panel-container">
          <h3 className="payment-method-panel-heading">Payment method for removal</h3>
          <PaymentMethodPanel
            paymentMethod={
              {
                ...paymentMethod,
                card: cardDetails,
              } as CreditCardPaymentMethod
            }
          />
        </div>
        <AssociatedBillingProfilesTable paymentMethodId={paymentMethodId} />
        <Form onSubmit={handleSubmit}>
          {({ formProps }) => {
            return (
              <form {...formProps}>
                {/* 
                TODO: Un-comment the code when these fields will be supported by API
                <FormSection>
                <Field
                  name="supportTicketLink"
                  label="Support ticket number"
                  isRequired={true}
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="supportTicketLink"
                        placeholder={'Add GSAC ticket number'}
                        testId="support-ticket-number"
                      />
                      {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                    </>
                  )}
                </Field>
              </FormSection> */}
                {/* <FormSection>
                <Field
                  name="internalComment"
                  label="Internal comment"
                  isRequired={true}
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="internalComment"
                        placeholder={'Add comments'}
                        testId="internal-comments"
                      />
                      {error && <ErrorMessage>This field is required</ErrorMessage>}
                    </>
                  )}
                </Field>
              </FormSection> */}
                <FormFooter align="end">
                  <ButtonGroup>
                    <Button appearance="subtle" testId="cancel-button" onClick={() => closeSidebar()}>
                      Cancel
                    </Button>
                    <LoadingButton testId="remove-button" isLoading={isProcessing} type="submit" appearance="primary">
                      Remove
                    </LoadingButton>
                  </ButtonGroup>
                </FormFooter>
              </form>
            );
          }}
        </Form>
      </div>
    );
  }
);

export default RemovePaymentMethod;
